import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useBranch } from "@/context/BranchContext";
import { numberFmt, rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, PackagePlus, AlertTriangle, Loader2 } from "lucide-react";

export default function InitialStock() {
  const { effectiveBranch, isAllBranch, currentBranchName } = useBranch();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [qty, setQty] = useState({});
  const [cost, setCost] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/stock", { params: effectiveBranch ? { branch_id: effectiveBranch } : {} }).then((r) => {
    setRows(r.data);
    const iq = {}, ic = {};
    r.data.forEach((p) => { iq[p.id] = ""; ic[p.id] = p.avg_cost ? String(Math.round(p.avg_cost)) : ""; });
    setQty(iq); setCost(ic);
  });
  useEffect(() => { if (effectiveBranch) load(); /* eslint-disable-next-line */ }, [effectiveBranch]);
  useEffect(() => { client.get("/categories").then((r) => setCats(r.data)); }, []);

  const filtered = useMemo(() => rows.filter((p) =>
    p.name.toLowerCase().includes(q.toLowerCase()) &&
    (catFilter === "all" || p.category_id === catFilter)
  ), [rows, q, catFilter]);

  const changed = rows.filter((p) => qty[p.id] !== "" && qty[p.id] != null);

  const submit = async () => {
    if (!changed.length) { toast.error("Isi minimal 1 stok awal"); return; }
    setSaving(true);
    try {
      await client.post("/stock/initial", {
        branch_id: effectiveBranch,
        items: changed.map((p) => ({ product_id: p.id, qty_base: Number(qty[p.id]), cost_per_base: Number(cost[p.id] || 0) })),
        note: "Stok awal",
      });
      toast.success(`Stok awal ${changed.length} produk tersimpan`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (isAllBranch && !effectiveBranch) {
    return <div className="flex h-full items-center justify-center p-6 text-center text-muted-foreground" data-testid="initial-pick-branch"><div><AlertTriangle className="mx-auto mb-2 h-8 w-8 opacity-40" />Pilih cabang untuk mengisi stok awal.</div></div>;
  }

  return (
    <div className="p-6 space-y-5 animate-in-up" data-testid="initial-stock-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Stok Awal Cepat</h1>
          <p className="text-sm text-muted-foreground">{currentBranchName} · Isi jumlah & HPP/modal per produk untuk menetapkan stok pembukaan</p>
        </div>
        <Button onClick={submit} disabled={saving || !changed.length} data-testid="submit-initial-stock">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PackagePlus className="mr-2 h-4 w-4" />}
          Simpan ({changed.length} produk)
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" className="pl-9" data-testid="initial-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48" data-testid="initial-cat-filter"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produk</TableHead>
              <TableHead>Satuan</TableHead>
              <TableHead className="text-right">Stok Saat Ini</TableHead>
              <TableHead className="w-40 text-right">Stok Awal</TableHead>
              <TableHead className="w-44 text-right">HPP / Modal (per satuan)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`initial-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.base_unit}</TableCell>
                <TableCell className="text-right font-mono">{numberFmt(p.stock_qty)}</TableCell>
                <TableCell className="text-right">
                  <Input type="number" value={qty[p.id] ?? ""} onChange={(e) => setQty((s) => ({ ...s, [p.id]: e.target.value }))}
                    className="h-9 w-28 text-right font-mono" placeholder="—" data-testid={`initial-qty-${p.id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <Input type="number" value={cost[p.id] ?? ""} onChange={(e) => setCost((s) => ({ ...s, [p.id]: e.target.value }))}
                    className="h-9 w-32 text-right font-mono" placeholder="0" data-testid={`initial-cost-${p.id}`} />
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Tidak ada produk</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">Catatan: HPP/modal dipakai untuk menghitung laba (COGS rata-rata tertimbang). Baris yang kolom "Stok Awal"-nya kosong tidak akan diubah.</p>
    </div>
  );
}
